import { useState } from 'react';
import {
  DEVICE_PLATFORM_LABEL,
  deviceDisplayName,
  formatKstMonthDay,
  formatLastVisit,
  type AccountDevices,
  type LicenseDevice,
} from '@platform/billing';
import { LuMonitor } from '@platform/icon';
import { showConfirm } from '@platform/ui';

import { useReleaseMyDevice } from '../api/use-release-device.mutation';
import { useRenameMyDevice } from '../api/use-rename-device.mutation';
import { DeviceNicknameField } from './DeviceNicknameField';

interface AccountDeviceListProps {
  data: AccountDevices;
  /**
   * 기기 정리 화면 — 오래 접속하지 않은 PC 를 위로 올리고, 자리가 찼으면 그 PC 에
   * "해제 추천"을 붙인다. 마이페이지는 서버 순서(최근 접속 순) 그대로 둔다.
   */
  cleanup?: boolean;
}

/**
 * 계정 등록 PC 목록 + 교체 횟수. 한도·횟수는 서버 응답을 그대로 보여준다 —
 * 화면에서 정책값을 계산하면 서버 정책이 바뀌는 순간 둘이 어긋난다.
 */
export function AccountDeviceList({ data, cleanup = false }: AccountDeviceListProps) {
  const { release, maxDevices } = data;
  const exhausted = release.remaining <= 0;
  const full = data.devices.length >= maxDevices;
  const devices = cleanup ? [...data.devices].sort(byOldestVisit) : data.devices;
  const recommendedId = cleanup && full ? devices[0]?.id : undefined;

  return (
    <section className="rounded-xl border border-black/[0.08] dark:border-white/10">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-black/[0.06] px-4 py-3 dark:border-white/10">
        <h3 className="text-sm font-bold text-adobe-dark dark:text-neutral-100">
          등록된 PC
        </h3>
        <SlotMeter used={data.devices.length} max={maxDevices} />
      </header>

      <div
        className={[
          'px-4 py-2.5 text-xs',
          exhausted
            ? 'bg-amber-50 text-amber-800 dark:bg-amber-500/10 dark:text-amber-200'
            : 'text-adobe-muted',
        ].join(' ')}
      >
        남은 교체 횟수 <b>{release.remaining}</b> / {release.quota}회 (최근{' '}
        {release.windowDays}일)
        {exhausted && release.nextAvailableAt && (
          <> · {formatKstMonthDay(release.nextAvailableAt)}부터 다시 교체할 수 있습니다</>
        )}
      </div>

      {devices.length === 0 ? (
        <p className="px-4 py-6 text-center text-sm text-adobe-muted">
          등록된 PC가 없습니다. 프로그램에 로그인하면 그 PC가 등록됩니다.
        </p>
      ) : (
        <ul className="divide-y divide-black/[0.06] dark:divide-white/10">
          {devices.map((d) => (
            <DeviceRow
              key={d.id}
              device={d}
              remaining={release.remaining}
              exhausted={exhausted}
              recommended={d.id === recommendedId}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

/** 접속 기록이 없는 PC 가 가장 오래된 것으로 친다. */
function byOldestVisit(a: LicenseDevice, b: LicenseDevice): number {
  const at = (d: LicenseDevice) => (d.lastSeenAt ? Date.parse(d.lastSeenAt) : 0);
  return at(a) - at(b);
}

function SlotMeter({ used, max }: { used: number; max: number }) {
  return (
    <div className="flex items-center gap-2" aria-label={`등록 ${used} / ${max}대`}>
      <div className="flex gap-1" aria-hidden>
        {Array.from({ length: max }, (_, i) => (
          <span
            key={i}
            className={[
              'h-1.5 w-5 rounded-full',
              i < used ? 'bg-brand-700 dark:bg-brand-300' : 'bg-neutral-200 dark:bg-white/15',
            ].join(' ')}
          />
        ))}
      </div>
      <span className="text-xs font-semibold text-adobe-muted">
        {used} / {max}대
      </span>
    </div>
  );
}

function DeviceRow({
  device,
  remaining,
  exhausted,
  recommended,
}: {
  device: LicenseDevice;
  remaining: number;
  exhausted: boolean;
  recommended: boolean;
}) {
  const release = useReleaseMyDevice();
  const rename = useRenameMyDevice();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const name = deviceDisplayName(device);

  const onRelease = async () => {
    const ok = await showConfirm({
      title: `${name} 해제`,
      message: `교체 횟수 1회가 차감됩니다(남은 횟수 ${remaining - 1}회). 해제한 PC에서는 최대 1시간 안에 프로그램 사용이 중지됩니다.`,
      confirmText: '해제',
      tone: 'danger',
    });
    if (ok) release.mutate(device.id);
  };

  const startEdit = () => {
    setDraft(device.nickname ?? '');
    setEditing(true);
  };

  const save = () =>
    rename.mutate(
      { deviceId: device.id, nickname: draft },
      { onSuccess: () => setEditing(false) },
    );

  return (
    <li className="px-4 py-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-neutral-100 text-neutral-600 dark:bg-white/10 dark:text-neutral-300">
            <LuMonitor className="h-4 w-4" aria-hidden />
          </span>
          <div className="min-w-0">
            <p className="flex items-center gap-2 text-sm font-semibold text-adobe-dark dark:text-neutral-100">
              <span className="truncate">{name}</span>
              {recommended && (
                <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-bold text-amber-900 dark:bg-amber-500/15 dark:text-amber-200">
                  해제 추천
                </span>
              )}
            </p>
            <p className="mt-0.5 text-xs font-semibold text-neutral-700 dark:text-neutral-200">
              {formatLastVisit(device.lastSeenAt)}
            </p>
            <p className="mt-0.5 text-xs text-adobe-muted">
              {/* 별명이 있으면 실제 PC 이름은 보조 정보로 남긴다 — 같은 별명 둘을 가를 수 있게. */}
              {device.nickname && device.hostname && `${device.hostname} · `}
              {DEVICE_PLATFORM_LABEL[device.platform]}
              {device.appVersion && ` · v${device.appVersion}`} ·{' '}
              {formatKstMonthDay(device.activatedAt)} 등록
            </p>
          </div>
        </div>
        {!editing && (
          <div className="flex shrink-0 gap-1.5">
            <button
              type="button"
              onClick={startEdit}
              className="rounded-lg px-2.5 py-1.5 text-xs font-bold text-adobe-muted transition-colors hover:bg-neutral-50 hover:text-adobe-dark dark:hover:bg-white/5 dark:hover:text-neutral-100"
            >
              이름 변경
            </button>
            <button
              type="button"
              disabled={exhausted || release.isPending}
              title={exhausted ? '교체 횟수를 모두 사용했습니다' : undefined}
              onClick={() => void onRelease()}
              className="rounded-lg border border-black/[0.12] px-3 py-1.5 text-xs font-bold text-adobe-dark transition-colors hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-white/15 dark:text-neutral-100 dark:hover:bg-white/5"
            >
              {release.isPending ? '해제 중…' : '해제'}
            </button>
          </div>
        )}
      </div>

      {editing && (
        <form
          className="mt-3 rounded-lg bg-neutral-50 p-3 dark:bg-white/5"
          onSubmit={(e) => {
            e.preventDefault();
            save();
          }}
        >
          <DeviceNicknameField
            value={draft}
            onChange={setDraft}
            autoFocus
            hint="비워 두면 PC 이름으로 표시됩니다. 기업 좌석을 배정받았다면 조직 관리자에게도 보입니다."
          />
          <div className="mt-3 flex justify-end gap-1.5">
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="rounded-lg px-3 py-1.5 text-xs font-bold text-adobe-muted hover:text-adobe-dark dark:hover:text-neutral-100"
            >
              취소
            </button>
            {/* brand-500 위 흰 글자는 AA 미달 — 700 을 쓴다. */}
            <button
              type="submit"
              disabled={rename.isPending}
              className="rounded-lg bg-brand-700 px-3 py-1.5 text-xs font-bold text-white hover:bg-brand-800 disabled:opacity-50"
            >
              {rename.isPending ? '저장 중…' : '저장'}
            </button>
          </div>
        </form>
      )}
    </li>
  );
}
